// react and js
import PropTypes from "prop-types";
import { React, useState, useRef, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

// mui
import {
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Link,
  Stack,
  Box,
} from "@mui/material";

// components
import ModalDialog from "./ModalDialog";
import Iconify from "./Iconify";

// hooks

// layouts

// pages

// theme
import { B1Small } from "../theme/styled";

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function IncludedSymbols({ plan, details }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  IncludedSymbols.propTypes = {
    plan: PropTypes.any.isRequired,
    details: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();

  // console.log(details);

  // eslint-disable-next-line
  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);

  const descriptionElementRef = useRef(null);
  useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  const [scroll, setScroll] = useState("paper");

  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };
  const handleClickClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Typography variant="contentSmallBold">
        {/* eslint-disable-next-line */}
        <Link
          component={RouterLink}
          onClick={handleClickOpen("paper")}
          underline="none"
          variant="contentSmallLink"
          color="black"
        >
          {t("components.newPlanItemTickData.includedSymbols")}
        </Link>
      </Typography>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">
                {t("components.newPlanItemTickData.includedSymbolsFor", {
                  plan: plan.plan_description.toLowerCase(),
                })}
              </Typography>

              <IconButton
                onClick={handleClickClose}
                sx={{ borderRadius: "10px" }}
              >
                <Iconify
                  icon="eva:close-outline"
                  minWidth={25}
                  minHeight={25}
                  color="#2E2E2E"
                />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                background: "#F2F2F2",
                borderTop: "1px solid #C8C8C8",
                borderBottom: "0px solid #C8C8C8",
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="contentMiddle">
                  {t("components.newPlanItemTickData.intro")}
                </Typography>

                <Typography variant="contentMiddle">
                  <br />
                  <br />
                  <Trans i18nKey="components.newPlanItemTickData.spreadInfo">
                    Each symbol tick data is provided in{" "}
                    <Typography
                      variant="contentMiddle"
                      sx={{ textDecoration: "underline" }}
                    >
                      low, medium and high spread
                    </Typography>{" "}
                    configuration. Please take a look into the
                    <Typography variant="contentMiddleBold">
                      <Link
                        variant="contentMiddleLink"
                        underline="none"
                        target="_blank"
                        color="inherit"
                        href={plan.user_manual_url}
                      >
                        {" user manual "}
                      </Link>
                    </Typography>
                    to get more information about precise spread values.
                  </Trans>
                </Typography>
              </Box>

              {details.map((row, index) => (
                <Stack
                  key={index}
                  spacing="3px"
                  sx={{ background: "white", mb: 1, px: "10px", py: "5px" }}
                >
                  <Box sx={{ display: "flex" }}>
                    <Box sx={{ width: "40%", pr: 1 }}>
                      <Typography variant="contentSmallBold">
                        {t("components.newPlanItemTickData.symbol")}
                      </Typography>
                    </Box>
                    <Box sx={{ width: "60%", pl: 1 }}>
                      <Typography variant="contentSmall">
                        {row.symbol_name}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex" }}>
                    <Box sx={{ width: "40%", pr: 1 }}>
                      <Typography variant="contentSmallBold">
                        {t("components.newPlanItemTickData.type")}
                      </Typography>
                    </Box>
                    <Box sx={{ width: "60%", pl: 1 }}>
                      <Typography variant="contentSmall">
                        {row.symbol_group}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex" }}>
                    <Box sx={{ width: "40%", pr: 1 }}>
                      <Typography variant="contentSmallBold">
                        {t("components.newPlanItemTickData.startDate")}
                      </Typography>
                    </Box>
                    <Box sx={{ width: "60%", pl: 1 }}>
                      <Typography variant="contentSmall">
                        {row.start_date}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              ))}
            </DialogContent>

            <DialogActions
              sx={{
                background: "#F2F2F2",
                p: 0,

                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <B1Small
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                onClick={handleClose}
                sx={{ py: 1 }}
              >
                {t("components.newPlanItemTickData.close")}
              </B1Small>
            </DialogActions>
          </>
        }
      />
    </>
  );
}

function PlanItemFeature({ feature }) {
  PlanItemFeature.propTypes = {
    feature: PropTypes.any.isRequired,
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        justifyContent: "start",
      }}
    >
      <Iconify
        color="#61CE70"
        icon="eva:checkmark-outline"
        minWidth={25}
        minHeight={25}
        sx={{ mr: 1, mt: "1px" }}
      />
      {feature}
    </Box>
  );
}

function NewPlanItemTickData({
  planInformation,
  categories,
  sponsored,
  reviseAction,
  isStopPossible,
}) {
  NewPlanItemTickData.propTypes = {
    planInformation: PropTypes.any.isRequired,
    categories: PropTypes.any.isRequired,
    sponsored: PropTypes.any.isRequired,
    reviseAction: PropTypes.any.isRequired,
    isStopPossible: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();

  const parts = planInformation.plan_fixed_price_value.split('.');
  const priceBeforeDot = parts[0];
  const priceAfterDot = parts[1] || '00';

  let paymentTenure = "";
  if (planInformation.plan_interval_unit === "DAY") {
    paymentTenure = t("components.newPlanItemTickData.tenure.daily");
  } else if (planInformation.plan_interval_unit === "WEEK") {
    paymentTenure = t("components.newPlanItemTickData.tenure.weekly");
  } else if (planInformation.plan_interval_unit === "MONTH") {
    paymentTenure = t("components.newPlanItemTickData.tenure.monthly");
  } else if (planInformation.plan_interval_unit === "YEAR") {
    paymentTenure = t("components.newPlanItemTickData.tenure.yearly");
  }

  let paymentCurrency = planInformation.plan_fixed_price_currency_code;
  if (planInformation.plan_fixed_price_currency_code === "EUR") {
    paymentCurrency = "€";
  }

  const categorySymbolGroups = {};
  categories[planInformation.t_tick_data_linking].forEach((item) => {
    const keys = Object.keys(categorySymbolGroups);
    if (keys.includes(item.symbol_group) === true) {
      categorySymbolGroups[item.symbol_group] += 1;
    } else {
      categorySymbolGroups[item.symbol_group] = 1;
    }
  });

  return (
    <Stack>
      <Box
        sx={{
          borderRadius: "10px",
          boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.6);",
          backgroundColor: "#C8C8C8",
        }}
      >
        <Box
          sx={{ pb: "10px", px: 2, mb: 1}}
          display="block"
          align="center"
        >
          <Typography
            variant="h5"
            display="block"
            align="center"
            sx={{ mt: "14px" }}
          >
            <Link
              variant="h5"
              underline="none"
              color="black"
              fontWeight={300}
              target="_blank"
              href={planInformation.home_url}
            >
              {planInformation.product_name}
            </Link>
          </Typography>

          <Typography color="black" fontWeight={400} variant="contentHuge">
            {planInformation.plan_description}
          </Typography>

          <br />
          <IncludedSymbols
            plan={planInformation}
            details={categories[planInformation.t_tick_data_linking]}
          />
        </Box>

        <Box sx={{ px: 2, pb: 2, backgroundColor: "#FFFFFF", borderRadius: "0px 0px 10px 10px", }}>
          {sponsored === false ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 3,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "end",
                    mb: "4px",
                  }}
                >
                  {paymentCurrency}
                </Typography>

                <Typography variant="h1" sx={{ lineHeight: 1 }}>
                  {priceBeforeDot}
                </Typography>

                <Typography variant="h3" sx={{ lineHeight: 1, mt: "7px" }}>
                  {priceAfterDot}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pb: 3,
                }}
              >
                <Typography variant="contentMiddle" sx={{ lineHeight: 1 }}>
                  {paymentTenure}{" "}
                  {reviseAction === "upgrade" && sponsored === false && isStopPossible === false ? (
                    <sup>*</sup>
                  ) : null}
                  {reviseAction === "reactivate" && sponsored === false && isStopPossible === false ? (
                    <sup>*</sup>
                  ) : null}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ pt: 3 }} />
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box>
              {/* eslint-disable-next-line */}
              {Object.keys(categorySymbolGroups).map((symbolGroup, index) => {
                return (
                  <PlanItemFeature
                    key={index}
                    feature={
                      <Typography variant="contentMiddle">
                        {categorySymbolGroups[symbolGroup] === 1
                          ? t("components.newPlanItemTickData.symbolCount.singular", {
                              count: categorySymbolGroups[symbolGroup],
                              group: symbolGroup,
                            })
                          : t("components.newPlanItemTickData.symbolCount.plural", {
                              count: categorySymbolGroups[symbolGroup],
                              group: symbolGroup,
                            })}{" "}
                        {reviseAction === "downgrade" && sponsored === false ? (
                          <sup>*</sup>
                        ) : null}
                      </Typography>
                    }
                  />
                );
              })}

              <Box sx={{ mt: 2 }} />

              <PlanItemFeature
                feature={
                  <Typography variant="contentMiddle">
                    <Trans i18nKey="components.newPlanItemTickData.spread.low">
                      <Typography variant="contentMiddleBold">Low spread </Typography>
                      data included
                    </Trans>
                  </Typography>
                }
              />

              <PlanItemFeature
                feature={
                  <Typography variant="contentMiddle">
                    <Trans i18nKey="components.newPlanItemTickData.spread.medium">
                      <Typography variant="contentMiddleBold">Medium spread </Typography>
                      data included
                    </Trans>
                  </Typography>
                }
              />

              <PlanItemFeature
                feature={
                  <Typography variant="contentMiddle">
                    <Trans i18nKey="components.newPlanItemTickData.spread.high">
                      <Typography variant="contentMiddleBold">High spread </Typography>
                      data included
                    </Trans>
                  </Typography>
                }
              />

              <Box sx={{ mt: 2 }} />

              <PlanItemFeature
                feature={
                  <Typography variant="contentMiddle">
                    <Trans i18nKey="components.newPlanItemTickData.executableInstaller">
                      <Typography variant="contentMiddleBold">Executable installer </Typography>
                      Executable installer for MetaTrader 5
                    </Trans>
                  </Typography>
                }
              />

              <Box sx={{ mt: 2 }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}

export default NewPlanItemTickData;
